import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Avatar,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { Contact } from '../types/Message';

interface Lead {
  id: number;
  name: string;
}

interface ContactFormProps {
  contact?: Contact | null;
  leads: Lead[];
  onSave: (data: Partial<Contact>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  leads,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<Partial<Contact>>({
    name: '',
    phone: '',
    email: '',
    whatsapp_number: '',
    instagram_handle: '',
    instagram_id: '',
    facebook_id: '',
    avatar: '',
    bio: '',
    is_active: true,
    notes: '',
    lead: null
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (contact) {
      setFormData(contact);
    }
  }, [contact]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = field === 'is_active' 
      ? (event.target as HTMLInputElement).checked
      : event.target.value;
    
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo
    if (errors[field]) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    // Validar pelo menos um canal de contato
    if (!formData.phone && !formData.email && !formData.whatsapp_number) {
      newErrors.phone = 'Pelo menos um canal de contato é necessário (telefone, email ou WhatsApp)';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validate()) return;

    try {
      await onSave(formData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Erro ao salvar contato' });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {contact ? 'Editar Contato' : 'Novo Contato'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Informações básicas */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                <Person sx={{ fontSize: 40 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1">Avatar do Contato</Typography>
                <Typography variant="caption" color="text.secondary">
                  URL da imagem de perfil
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome *"
              value={formData.name || ''}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Lead Relacionado"
              select
              SelectProps={{ native: true }}
              value={formData.lead || ''}
              onChange={handleChange('lead')}
              disabled={loading}
            >
              <option value="">Nenhum</option>
              {leads.map(lead => (
                <option key={lead.id} value={lead.id}>
                  {lead.name}
                </option>
              ))}
            </TextField>
          </Grid>

          {/* Canais de comunicação */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Canais de Comunicação
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Telefone"
              value={formData.phone || ''}
              onChange={handleChange('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              placeholder="(11) 99999-9999"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="WhatsApp"
              value={formData.whatsapp_number || ''}
              onChange={handleChange('whatsapp_number')}
              placeholder="(11) 99999-9999"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              disabled={loading}
            />
          </Grid>

          {/* Redes sociais */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Redes Sociais
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Instagram Handle"
              value={formData.instagram_handle || ''}
              onChange={handleChange('instagram_handle')}
              placeholder="@usuario"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Instagram ID"
              value={formData.instagram_id || ''}
              onChange={handleChange('instagram_id')}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Facebook ID"
              value={formData.facebook_id || ''}
              onChange={handleChange('facebook_id')}
              disabled={loading}
            />
          </Grid>

          {/* Informações adicionais */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL do Avatar"
              value={formData.avatar || ''}
              onChange={handleChange('avatar')}
              placeholder="https://..."
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Biografia"
              multiline
              rows={3}
              value={formData.bio || ''}
              onChange={handleChange('bio')}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={handleChange('notes')}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active || false}
                  onChange={handleChange('is_active')}
                  disabled={loading}
                />
              }
              label="Contato ativo"
            />
          </Grid>

          {/* Erro de submissão */}
          {errors.submit && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {errors.submit}
              </Typography>
            </Grid>
          )}

          {/* Botões */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                onClick={onCancel}
                disabled={loading}
                variant="outlined"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : undefined}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default ContactForm;
